'use client';

import { Key, ReactNode, useEffect, useRef, useState } from 'react';
import { ComboBox } from 'react-aria-components';

import { IconKitCloseCircle } from '@/icon/kit/CloseCircle';
import { IconKitSearch } from '@/icon/kit/Search';
import { UiKitButton } from '@/ui-kit/button';
import { UiKitInputSearch } from '@/ui-kit/input-search';
import { Option, UiKitListbox } from '@/ui-kit/listbox';

import { styles } from './UiKitAutocomplete.styles';

export interface UiKitAutocompleteProps<T extends Option> {
  options: T[];
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  onChange?: (value: string) => void;
  value?: string;
  errorMessage?: string;
  onSelect?: (key: Key | null) => void;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  renderOption?: (option: T) => ReactNode;
  className?: string;
  name?: string;
  classNameInput?: string;
  wrapperClassNameInput?: string;
  state?: 'default' | 'map';
  required?: boolean;
  error?: boolean;
  allowCustomValue?: boolean;
  menuTrigger?: 'focus' | 'input' | 'manual';
  allowsEmptyCollection?: boolean;
  notResetValue?: boolean;
  onBlur?: () => void;
  optionClassName?: string;
}

export function UiKitAutocomplete<T extends Option>({
  options,
  disabled,
  onChange,
  value,
  helperText,
  errorMessage,
  placeholder,
  label,
  onSelect,
  startAdornment,
  endAdornment,
  renderOption,
  className,
  name,
  classNameInput,
  wrapperClassNameInput,
  state = 'default',
  required,
  error,
  allowCustomValue = true,
  menuTrigger = 'focus',
  allowsEmptyCollection = true,
  notResetValue = false,
  onBlur,
  optionClassName
}: UiKitAutocompleteProps<T>) {
  const [inputValue, setInputValue] = useState(value ?? '');
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInputValue(value ?? '');
  }, [value]);

  const slots = styles({ empty: inputValue === '', focused: isFocused });

  const triggerRef = useRef<HTMLDivElement>(null);

  const handleClear = () => {
    onChange?.('');
    setInputValue('');
  };

  const handleChange = (newValue: string) => {
    // это нужно для того, чтобы при отправке формы не сбрасывалось значение
    const isFocused = triggerRef.current?.contains(document.activeElement);

    if (!isFocused && newValue === '' && notResetValue) {
      return;
    }

    setInputValue(newValue);
    onChange?.(newValue);
  };

  return (
    <div className={slots.base({ className })}>
      <ComboBox
        onInputChange={handleChange}
        inputValue={inputValue}
        allowsCustomValue={allowCustomValue}
        isDisabled={disabled}
        isInvalid={!!errorMessage}
        menuTrigger={menuTrigger}
        className={slots.menu()}
        onSelectionChange={onSelect}
        defaultFilter={() => true}
        allowsEmptyCollection={allowsEmptyCollection}
      >
        <UiKitInputSearch
          ref={triggerRef}
          helperText={helperText}
          errorMessage={errorMessage}
          placeholder={placeholder}
          label={label}
          startAdornment={startAdornment}
          name={name}
          className={slots.input({ className: classNameInput })}
          wrapperClassName={wrapperClassNameInput}
          error={error}
          endAdornment={
            <>
              {endAdornment}
              {state === 'map' && (
                <>
                  <IconKitSearch
                    width={24}
                    height={24}
                    className={slots.iconSearch()}
                  />
                  <UiKitButton
                    color="ghost"
                    rounded="full"
                    className={slots.button()}
                    onClick={handleClear}
                  >
                    <IconKitCloseCircle className={slots.closeIcon()} />
                  </UiKitButton>
                </>
              )}
            </>
          }
          required={required}
          onBlur={onBlur ? onBlur : handleBlur}
          onFocus={handleFocus}
        />
        <UiKitListbox
          options={options}
          renderOption={renderOption}
          triggerRef={triggerRef}
          classNameState={state}
          optionClassName={optionClassName}
        />
      </ComboBox>
    </div>
  );
}
